// @ts-check
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Drawer, DrawerContent, DrawerContentBody, DrawerPanelContent, DrawerCloseButton, DrawerHead, DrawerActions, Flex, FlexItem } from '@patternfly/react-core';
import _ from 'lodash';
import './SignalSelector.css';
import ModelsContainer from '../Model/ModelsContainer';
import { getColors } from '../../utils/colors';
import AvailableSignals from './AvailableSignals';
import ChosenSignals from './ChosenSignals';
import PropTypes from 'prop-types';
import { Icons } from '../Common';

const RIGHT_TELEMETRY_DRAWER = 'right-telemetry-drawer-width';

const onResizeRightPanelContent = (width) => {
  localStorage.setItem(RIGHT_TELEMETRY_DRAWER, `${width}px`);
};

const rightPanelDrawerDefaultSize = localStorage.getItem(RIGHT_TELEMETRY_DRAWER) || '275px';

const flattenTree = tree => {
  let result = [];

  _.forEach(tree, item => {
    result.push(item);
    if (item.children) {
      result = result.concat(flattenTree(item.children));
    }
  });

  return result;
};

const filterItems = (item, checkedItem) => {

  if (item.id === checkedItem.id) {
    return true;
  }

  if (item.children) {
    return (
      (item.children = item.children
        .map(opt => Object.assign({}, opt))
        .filter(child => filterItems(child, checkedItem))).length > 0
    );
  }
};

const SignalItem = ({ signal, signalIndex, models, onSignalChanged, onRemoveSignal }) => {
  const _onRemoveSignal = useCallback(() => onRemoveSignal(signal), [onRemoveSignal, signal]);
  const _onSignalChanged = useCallback((changedSignal) => {
    onSignalChanged(changedSignal, signalIndex);
  }, [onSignalChanged, signalIndex]);

  return (
    <Flex alignItems={{ default: 'alignItemsCenter' }}>
      <FlexItem>
        <Icons.AngleLeftIcon className="signal-angle-left-icon" onClick={_onRemoveSignal} />
      </FlexItem>
      <FlexItem shrink={{ default: 'shrink' }}>
        <div className="signal-breadcrumb-wrapper">
          <div>{ signal.name }</div>
          <div className="signal-breadcrumb">{ signal.breadcrumb }</div>
        </div>
      </FlexItem>
      <FlexItem>
        <ModelsContainer models={models} data={signal} setData={_onSignalChanged} />
      </FlexItem>
    </Flex>
  );
};

SignalItem.propTypes = {
  signal: PropTypes.object.isRequired,
  models: PropTypes.array,
  onSignalChanged: PropTypes.func.isRequired,
  onRemoveSignal: PropTypes.func.isRequired
};

const SignalSelector = ({ tree, isDrawerExpanded, children, disableApplyButton, setDisableApplyButton, setChosenSignals, chartViewData, availableSchemas, onCloseClick }) => {
  const [checkedTreeItems, setCheckedTreeItems] = useState([]);
  const drawerRef = useRef(null);
  // const [searchInput, setSearchInput] = useState('');

  const onExpand = useCallback(() => {
    drawerRef.current?.focus();
  }, []);

  const onTreeCheck = (evt, treeItem) => {
    setDisableApplyButton(false);
    const checked = evt.target.checked;

    const checkedItemTree = tree.map(opt => Object.assign({}, opt)).filter(item => filterItems(item, treeItem));
    const flatCheckedItems = flattenTree(checkedItemTree);

    setCheckedTreeItems(prevState => {
      const usedColors = prevState.map(item => item.color);
      if (checked) {
        const telemetrySignals = flatCheckedItems.filter(item => item.isTelemetry);
        const newSignals = _.differenceBy(telemetrySignals, prevState, 'id');
        const newColors = getColors({ usedColors, numColors: newSignals.length });
        const newSignalsWithColors = newSignals.map((item, index) => ({
          ...item,
          color: newColors[index]
        }));
        return [...prevState, ...newSignalsWithColors];
      }

      return prevState.filter(item => !flatCheckedItems.some(i => i.id === item.id));
    });
  };

  const isChecked = dataItem => checkedTreeItems.some(item => item.id === dataItem.id);

  const areAllDescendantsChecked = dataItem => dataItem.children ? dataItem.children.every(child => areAllDescendantsChecked(child)) : isChecked(dataItem);
  const areSomeDescendantsChecked = dataItem => dataItem.children ? dataItem.children.some(child => areSomeDescendantsChecked(child)) : isChecked(dataItem);

  const mapTree = item => {
    const hasCheck = areAllDescendantsChecked(item);
    item.checkProps.checked = false;

    if (hasCheck) {
      item.checkProps.checked = true;
    } else {
      const hasPartialCheck = areSomeDescendantsChecked(item);
      if (hasPartialCheck) {
        item.checkProps.checked = null;
      }
    }

    if (item.children) {
      return {
        ...item,
        children: item.children.map(child => mapTree(child))
      };
    }
    return item;
  };

  const mappedTree = tree.map(item => mapTree(item));

  const removeItemInCheckedTreeItems = useCallback((item) => {
    setDisableApplyButton(false);
    setCheckedTreeItems(prevState => _.reject(prevState, { id: item.id }));
  }, [setDisableApplyButton]);

  const handleApplySignals = useCallback(() => {

    const filteredTrees = _.filter(checkedTreeItems, item => item.isTelemetry && availableSchemas.includes(item.type));

    if (!_.isEmpty(filteredTrees)) {

      let filterTelemetry = _.filter(filteredTrees, item => item.isTelemetry);

      const alreadyGroupedList = [];

      const groupedAssets = _.compact(_.map(filterTelemetry, item => {
        const assetId = item.id.split('/')[0];

        if (!alreadyGroupedList.includes(assetId)) {
          alreadyGroupedList.push(assetId);

          return {
            assetId,
            telemetries: _.filter(filterTelemetry, i => i.id.split('/')[0] === assetId)
          };
        }

        return null;
      }));

      const l = _.map(groupedAssets, asset => {

        return {
          assetId: asset.assetId,
          signals: _.map(asset.telemetries, telemetry => {
            const ids = telemetry.id.split('/');

            const belongsToAComponent = ids.length === 3;

            if (belongsToAComponent) {
              return {
                name: ids[2],
                component: ids[1],
                type: telemetry.type,
                fullName: telemetry.graphName,
                laneNumber: telemetry.laneNumber,
                color: telemetry.color,
                decimalPoint: telemetry.decimalPoint
              };
            }

            return {
              name: ids[1],
              component: null,
              type: telemetry.type,
              fullName: telemetry.graphName,
              laneNumber: telemetry.laneNumber,
              color: telemetry.color,
              decimalPoint: telemetry.decimalPoint
            };
          })
        };
      });

      return setChosenSignals(l);
    }

    setChosenSignals(null);

  }, [checkedTreeItems, setChosenSignals, availableSchemas]);

  const handleRemoveAllSignals = () => {
    setCheckedTreeItems([]);
    setDisableApplyButton(false);
  };

  const onChangeData = useCallback((signal, signalIndex) => {
    setCheckedTreeItems(prevState => {
      const newState = [...prevState];

      newState[signalIndex] = signal;

      return newState;
    });
    setDisableApplyButton(false);
  }, [setDisableApplyButton]);

  const columns = useMemo(/** @returns {import('components/Common/SimpleTable/SimpleTable').ColumnType<object>[]} */ () => {
    return [
      {
        path: '.',
        label: '',
        render: (signal, unused, signalIndex) => {
          return (<SignalItem
            signal={signal}
            signalIndex={signalIndex}
            onSignalChanged={onChangeData}
            onRemoveSignal={removeItemInCheckedTreeItems}
            models={chartViewData.models}
          />);
        }
      }
    ];
  }, [chartViewData, onChangeData, removeItemInCheckedTreeItems]);


  const tableData = useMemo(() => {
    const items = _.filter(checkedTreeItems, item => {
      return item.isTelemetry && availableSchemas.includes(item.type);
    });
    return items;

  }, [checkedTreeItems, availableSchemas]);

  // const onSearchInput = useCallback((input) => {
  //   setSearchInput(input);
  // }, []);

  // const filteredSignals = useMemo(() => {
  //   if (!_.isEmpty(searchInput)) {
  //     const lcInput = searchInput.toLocaleLowerCase();
  //     return _.filter(tableData, item => {
  //       return (item.name.toLocaleLowerCase().indexOf(lcInput) !== -1);
  //     });
  //   }
  //   return tableData;
  // }, [searchInput, tableData]);

  return (
    <>
      <Drawer
        isExpanded={isDrawerExpanded}
        onExpand={onExpand}
        position="left"
      >
        <DrawerContent
          panelContent={
            <AvailableSignals
              mappedTree={mappedTree}
              onTreeCheck={onTreeCheck}
              onCloseClick={onCloseClick}
            />
          }>
          <DrawerContentBody className="multiple-chart-graph-component">
            { children }
          </DrawerContentBody>
        </DrawerContent>
      </Drawer>
      <Drawer
        isExpanded={isDrawerExpanded}
        onExpand={onExpand}
        position="right"
        className="drawer-signal-selector"
      >
        <DrawerContent
          className="drawer-signal-selector-content"
          panelContent={
            <DrawerPanelContent className="drawer-signal-selector-content" isResizable onResize={(_event, width) => onResizeRightPanelContent(width)} defaultSize={rightPanelDrawerDefaultSize} minSize="150px" maxSize="450px">
              <DrawerHead>
                { /* <SearchInput value={searchInput} onChange={onSearchInput} onClear={() => onSearchInput('')}/> */ }
                <DrawerActions>
                  <DrawerCloseButton onClick={onCloseClick}/>
                </DrawerActions>
              </DrawerHead>
              <ChosenSignals
                handleRemoveAllSignals={handleRemoveAllSignals}
                checkedTreeItems={checkedTreeItems}
                handleApplySignals={handleApplySignals}
                disableApplyBtn={disableApplyButton}
                tableData={tableData}
                columns={columns}
              />
            </DrawerPanelContent>
          }
          style={{ zIndex: 0 }}
        />
      </Drawer>
    </>
  );
};

export default SignalSelector;
